
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { API_ENDPOINT } from "@/config/environment";
import { CopyIcon } from "lucide-react";
import { useState } from "react";

export const APIInformation = () => {
  const [copied, setCopied] = useState(false);
  
  const endpoint = `${API_ENDPOINT}/add-school`;
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exampleRequest = {
    school_id: "12345",
    nama_sekolah: "Example School Name",
    pordi_school_id: "P123" // Optional
  };

  const exampleRequestString = JSON.stringify(exampleRequest, null, 2);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>School API Documentation</CardTitle>
        <CardDescription>
          Use this API to programmatically add new schools to the system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-1">API Endpoint</h3>
          <div className="flex items-center gap-2">
            <code className="bg-gray-100 p-2 rounded flex-1 text-sm">{endpoint}</code>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => copyToClipboard(endpoint)}
            >
              <CopyIcon className="h-4 w-4 mr-1" />
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-1">Request Method</h3>
          <code className="bg-gray-100 p-2 rounded text-sm">POST</code>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-1">Headers</h3>
          <code className="bg-gray-100 p-2 rounded block text-sm">
            Content-Type: application/json<br />
            Authorization: Bearer [your_supabase_anon_key]
          </code>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-1">Request Body</h3>
          <div className="flex items-start gap-2">
            <pre className="bg-gray-100 p-2 rounded flex-1 text-sm overflow-x-auto">
              {exampleRequestString}
            </pre>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2" 
              onClick={() => copyToClipboard(exampleRequestString)}
            >
              <CopyIcon className="h-4 w-4 mr-1" />
              Copy
            </Button>
          </div>
          <div className="mt-4 text-sm">
            <p className="font-medium">Required fields:</p>
            <ul className="list-disc pl-5">
              <li><code>school_id</code>: string - Unique identifier for the school (required)</li>
              <li><code>nama_sekolah</code>: string - School name (required)</li>
            </ul>
            <p className="font-medium mt-2">Optional fields:</p>
            <ul className="list-disc pl-5">
              <li><code>pordi_school_id</code>: string - Optional external ID reference</li>
            </ul>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-1">Response</h3>
          <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
{`{
  "success": true,
  "message": "School created successfully",
  "data": {
    "school": {
      "id": "uuid",
      "school_id": "12345",
      "nama_sekolah": "Example School Name",
      "pordi_school_id": "P123",
      "created_at": "2025-05-08T12:34:56.789Z"
    }
  }
}`}
          </pre>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-1">Error Responses</h3>
          <div className="space-y-3">
            <div>
              <p className="font-medium">400 Bad Request</p>
              <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
{`{
  "success": false,
  "error": "School ID is required"
}`}
              </pre>
            </div>
            
            <div>
              <p className="font-medium">409 Conflict</p>
              <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
{`{
  "success": false,
  "error": "School with ID 12345 already exists"
}`}
              </pre>
            </div>
            
            <div>
              <p className="font-medium">500 Internal Server Error</p>
              <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
{`{
  "success": false,
  "error": "Error creating school"
}`}
              </pre>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
